/**
 * Per-tab extensions of the base ``WorkspaceSearchSchema`` — used by the
 * 6 workspace surfaces to round-trip their typed search + filter chip
 * state through the URL.
 *
 * Design rationale (May 28, 2026):
 *   - The base ``WorkspaceSearchSchema`` (``./searchSchema.ts``) keeps
 *     the cross-cutting params (sub / focus / filter / msg / proposal)
 *     in one place. Per-tab extensions add ONLY the typed filter chip
 *     fields each surface needs.
 *   - Every per-tab schema lives in this single file so the URL contract
 *     is auditable in one read — adding a new chip is a 3-line patch
 *     here + an entry in the matching ``<TabSearchFilters>`` component.
 *   - Schemas are exported as a ``validate*Search`` function ready to
 *     drop into ``createFileRoute(...)({ validateSearch: ... })``.
 *
 * URL encoding rules — mirror ``buildSearchParams`` in
 * ``@ando/core/utils``:
 *   - CSV array: ``?provider=email,slack`` (NOT repeated keys)
 *   - boolean:   ``?unread=true`` / ``?unread=false`` (key absent → false)
 *   - number:    ``?amount_min=100`` (NaN / non-finite → absent)
 *   - string:    URL-decoded as-is, ``""`` → absent
 *
 * IMPORTANT — all fields are OPTIONAL on the URL but the
 * ``validate*Search`` functions normalise undefined to sensible
 * defaults (empty array / false / null / "all" / "lexical") so the
 * downstream route component never needs ``??`` everywhere.
 */

import { parseCSV } from "@ando/core";
import { z } from "zod";

import { type WorkspaceSearch, WorkspaceSearchSchema } from "./searchSchema";

// ---------------------------------------------------------------------------
// Coercion helpers — keep validateSearch logic out of every per-tab block.
// ---------------------------------------------------------------------------

/** Parse "?key=true" / "?key=false" — anything else → ``false``. */
function asBool(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  return value === "true";
}

/** Parse "?key=12.5" — non-finite / unparseable → ``null``. */
function asNumberOrNull(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string" || value.length === 0) return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Parse "?key=foo" — anything non-string / empty → ``""``. */
function asString(value: unknown): string {
  if (typeof value === "string") return value;
  return "";
}

/**
 * Coerce a CSV URL value into a string-literal array — drops any value
 * not present in ``vocabulary``. Use this for every enum chip filter so
 * the route component receives a strongly-typed array without an
 * explicit ``as`` cast.
 */
function asEnumArray<T extends string>(
  value: unknown,
  vocabulary: ReadonlyArray<T>,
): ReadonlyArray<T> {
  const parts = parseCSV(value);
  if (parts.length === 0) return [];
  const allowed = new Set<string>(vocabulary);
  return parts.filter((v): v is T => allowed.has(v));
}

/** Coerce ``?period=today|7d|30d|all`` → narrow union. */
function asPeriod(value: unknown): "today" | "7d" | "30d" | "all" {
  if (value === "today" || value === "7d" || value === "30d") return value;
  return "all";
}

// ---------------------------------------------------------------------------
// Conversations search schema.
// ---------------------------------------------------------------------------

const CONVERSATION_PROVIDERS = ["email", "slack", "teams", "google_chat", "pec"] as const;
type ConversationProvider = (typeof CONVERSATION_PROVIDERS)[number];

/**
 * NOTE: all extension fields are declared OPTIONAL (with `?`) so existing
 * navigation call-sites that only pass ``{ focus: ... }`` / ``{ msg: ... }``
 * continue to typecheck against TanStack Router's strict ``MakeRequiredSearchParams``
 * contract. The ``validate*Search`` parser ALWAYS materialises the fields
 * with sensible defaults, so route-component reads stay non-null.
 */
/** Inbox smart-view ids — mirrors ``INBOX_SMART_VIEWS`` in
 *  ``features/workspace/inbox/utils/inboxSmartViews.ts``. */
const CONVERSATIONS_SMART_VIEWS = [
  "tutti",
  "unread",
  "needs_reply",
  "attachments",
  "starred",
  "today",
] as const;
type ConversationsSmartView = (typeof CONVERSATIONS_SMART_VIEWS)[number];

/** Grouping axes — paired with the segmented ``InboxGroupingToggle``. */
const CONVERSATIONS_GROUP_MODES = ["thread", "flat"] as const;
type ConversationsGroupMode = (typeof CONVERSATIONS_GROUP_MODES)[number];


export interface ConversationsSearch extends WorkspaceSearch {
  readonly q?: string;
  /**
   * URL key intentionally PLURAL — keeps the legacy ``?provider=gmail``
   * passthrough param (written by the OAuth callback in ``auth/callback.tsx``)
   * disambiguated from the multi-select filter chip.
   */
  readonly providers?: ReadonlyArray<ConversationProvider>;
  readonly is_unread?: boolean;
  readonly has_attachments?: boolean;
  readonly is_important?: boolean;
  readonly period?: "today" | "7d" | "30d" | "all";
  /** Smart view (default ``tutti``). */
  readonly view?: ConversationsSmartView;
  /** Grouping axis (default ``thread``). */
  readonly group?: ConversationsGroupMode;
  /**
   * Email direction (Jul 2026). ``"sent"`` = the "Inviate" view (provider SENT
   * folder); omit / ``"received"`` = INBOX. Lives in the Filtri popover.
   */
  readonly direction?: "sent" | "received";
  /**
   * Deep-link seed for the AI composer. When non-empty, the conversations
   * route ingests the string into ``setComposerDraft`` on mount and strips
   * the param from the URL so a refresh doesn't re-seed the same prompt.
   * Used by cross-surface CTAs ("Chiedi a Ando dei dati Cassetto" → open
   * chat already typed) so connectors can hand users off to the AI without
   * forcing them to compose the question from scratch. Cap ~2000 chars to
   * stay inside HTTP-URL practical limits.
   */
  readonly prompt?: string;
}

export function validateConversationsSearch(raw: Record<string, unknown>): ConversationsSearch {
  const base = WorkspaceSearchSchema.parse(raw);
  const rawPrompt = asString(raw.prompt);
  const rawView = typeof raw.view === "string" ? raw.view : undefined;
  const view: ConversationsSmartView =
    rawView && (CONVERSATIONS_SMART_VIEWS as readonly string[]).includes(rawView)
      ? (rawView as ConversationsSmartView)
      : "tutti";
  const rawGroup = typeof raw.group === "string" ? raw.group : undefined;
  const group: ConversationsGroupMode =
    rawGroup && (CONVERSATIONS_GROUP_MODES as readonly string[]).includes(rawGroup)
      ? (rawGroup as ConversationsGroupMode)
      : "thread";
  return {
    ...base,
    q: asString(raw.q),
    providers: asEnumArray(raw.providers, CONVERSATION_PROVIDERS),
    is_unread: asBool(raw.unread),
    has_attachments: asBool(raw.has_attachments),
    is_important: asBool(raw.is_important),
    period: asPeriod(raw.period),
    view,
    group,
    direction:
      raw.direction === "sent" ? "sent" : raw.direction === "received" ? "received" : undefined,
    // Cap pathological deep-links so we never paste a 50KB string into the
    // composer. The 2000-char ceiling matches the practical HTTP URL limit.
    prompt: rawPrompt && rawPrompt.length <= 2000 ? rawPrompt : undefined,
  };
}

// ---------------------------------------------------------------------------
// Documents search schema.
// ---------------------------------------------------------------------------

const DOCUMENT_SOURCES = [
  "drive",
  "onedrive",
  "sharepoint",
  "email_attachment",
  "chat_attachment",
  "manual_upload",
  "ai_generated",
] as const;
type DocumentSource = (typeof DOCUMENT_SOURCES)[number];

const DOCUMENT_MIMES = ["docx", "xlsx", "pptx", "pdf"] as const;
type DocumentMime = (typeof DOCUMENT_MIMES)[number];

/** Smart-view ids — mirrors ``SMART_VIEWS`` in ``features/workspace/documents/utils/smartViews.ts``. */
const DOCUMENT_VIEWS = ["tutti", "ricevuti", "drive", "ando", "pinnati"] as const;
type DocumentView = (typeof DOCUMENT_VIEWS)[number];

/** Recency buckets — mirrors ``RecencyBucket`` in ``features/workspace/documents/utils/recencyGroup.ts``. */
const DOCUMENT_RECENCY_BUCKETS = ["today", "week", "month", "older"] as const;
type DocumentRecencyBucket = (typeof DOCUMENT_RECENCY_BUCKETS)[number];

/** Source buckets — mirrors ``SourceBucket`` in ``features/workspace/documents/utils/sourceGroup.ts``. */
const DOCUMENT_SOURCE_BUCKETS = [
  "google_drive",
  "onedrive",
  "sharepoint",
  "email",
  "chat",
  "manual_upload",
  "filesystem",
  "ai_generated",
  "other",
] as const;
type DocumentSourceBucket = (typeof DOCUMENT_SOURCE_BUCKETS)[number];

/** Grouping axes — paired with the segmented toggle in the toolbar.
 *  Jun 24, 2026 — added "folder" for the Finder-style folder browser. */
const DOCUMENT_GROUP_MODES = ["source", "recency", "folder"] as const;
type DocumentGroupMode = (typeof DOCUMENT_GROUP_MODES)[number];

export interface DocumentsSearch extends WorkspaceSearch {
  readonly q?: string;
  readonly source?: ReadonlyArray<DocumentSource>;
  readonly mime?: ReadonlyArray<DocumentMime>;
  readonly period?: "today" | "7d" | "30d" | "all";
  readonly mode?: "lexical" | "semantic";
  /** Active smart view — drives the SmartSourceChips strip. */
  readonly view?: DocumentView;
  /** Top-level grouping axis (default ``source``). */
  readonly group?: DocumentGroupMode;
  /** Collapsed recency buckets (URL-CSV). Default = empty (all expanded). */
  readonly collapsed?: ReadonlyArray<DocumentRecencyBucket>;
  /** Collapsed source buckets (URL-CSV). Default = collapsed-by-default set
   *  resolved by ``SOURCE_DEFAULT_EXPANDED``. */
  readonly collapsed_source?: ReadonlyArray<DocumentSourceBucket>;
  /**
   * Active Drive-folder filter — encoded as ``<drive_provider>::<path>``
   * (e.g. ``google_drive::Clienti/2026/Brescia``). The FE narrows the
   * document list to rows whose ``drive_provider`` matches AND whose
   * ``folder_path`` starts with the selected path. ``undefined`` = no
   * folder filter (all tracked folders visible). Decoded via
   * :func:`parseFolderFilter` in ``utils/folderPath.ts``.
   */
  readonly folder?: string;
}

export function validateDocumentsSearch(raw: Record<string, unknown>): DocumentsSearch {
  const base = WorkspaceSearchSchema.parse(raw);
  const rawView = typeof raw.view === "string" ? raw.view : undefined;
  const view: DocumentView =
    rawView && (DOCUMENT_VIEWS as readonly string[]).includes(rawView)
      ? (rawView as DocumentView)
      : "tutti";
  const rawGroup = typeof raw.group === "string" ? raw.group : undefined;
  // Jun 24, 2026 — default switched to "folder" to land on the
  // Finder-style folder browser. Must stay in sync with
  // ``DEFAULT_GROUPING_MODE`` in
  // ``apps/client/src/features/workspace/documents/utils/sourceGroup.ts``
  // — otherwise the validator coerces ``group=undefined`` to "source"
  // and the toggle can't toggle back to folder because
  // ``encodeGroupingMode(folder)`` returns ``undefined`` (the URL gets
  // stripped, the validator re-coerces to source, the FE renders
  // "Per fonte" instead of "Per cartella").
  const group: DocumentGroupMode =
    rawGroup && (DOCUMENT_GROUP_MODES as readonly string[]).includes(rawGroup)
      ? (rawGroup as DocumentGroupMode)
      : "folder";
  return {
    ...base,
    q: asString(raw.q),
    source: asEnumArray(raw.source, DOCUMENT_SOURCES),
    mime: asEnumArray(raw.mime, DOCUMENT_MIMES),
    period: asPeriod(raw.period),
    mode: raw.mode === "semantic" ? "semantic" : "lexical",
    view,
    group,
    collapsed: asEnumArray(raw.collapsed, DOCUMENT_RECENCY_BUCKETS),
    collapsed_source: asEnumArray(raw.collapsed_source, DOCUMENT_SOURCE_BUCKETS),
    folder: asString(raw.folder),
  };
}

// ---------------------------------------------------------------------------
// Finance search schema.
// ---------------------------------------------------------------------------

const FINANCE_TYPES = ["fattura", "banking", "f24", "pagamento"] as const;
type FinanceType = (typeof FINANCE_TYPES)[number];

const FINANCE_STATUS = ["paid", "pending", "overdue"] as const;
type FinanceStatus = (typeof FINANCE_STATUS)[number];

export interface FinanceSearch extends WorkspaceSearch {
  readonly q?: string;
  readonly type?: ReadonlyArray<FinanceType>;
  readonly status?: ReadonlyArray<FinanceStatus>;
  readonly period?: "today" | "7d" | "30d" | "all";
  readonly amount_min?: number | null;
  readonly amount_max?: number | null;
}

export function validateFinanceSearch(raw: Record<string, unknown>): FinanceSearch {
  const base = WorkspaceSearchSchema.parse(raw);
  return {
    ...base,
    q: asString(raw.q),
    type: asEnumArray(raw.type, FINANCE_TYPES),
    status: asEnumArray(raw.status, FINANCE_STATUS),
    period: asPeriod(raw.period),
    amount_min: asNumberOrNull(raw.amount_min),
    amount_max: asNumberOrNull(raw.amount_max),
  };
}

// ---------------------------------------------------------------------------
// Calendar search schema.
// ---------------------------------------------------------------------------

const CALENDAR_TYPES = ["meeting", "holiday", "reminder"] as const;
type CalendarType = (typeof CALENDAR_TYPES)[number];

const CALENDAR_PROVIDERS = ["google", "outlook"] as const;
type CalendarProvider = (typeof CALENDAR_PROVIDERS)[number];

export interface CalendarSearch extends WorkspaceSearch {
  readonly q?: string;
  readonly type?: ReadonlyArray<CalendarType>;
  readonly provider?: ReadonlyArray<CalendarProvider>;
  readonly period?: "today" | "7d" | "30d" | "all";
}

export function validateCalendarSearch(raw: Record<string, unknown>): CalendarSearch {
  const base = WorkspaceSearchSchema.parse(raw);
  return {
    ...base,
    q: asString(raw.q),
    type: asEnumArray(raw.type, CALENDAR_TYPES),
    provider: asEnumArray(raw.provider, CALENDAR_PROVIDERS),
    period: asPeriod(raw.period),
  };
}

// ---------------------------------------------------------------------------
// Work (tasks) search schema.
// ---------------------------------------------------------------------------

const WORK_STATUS = ["today", "upcoming", "overdue", "completed"] as const;
type WorkStatus = (typeof WORK_STATUS)[number];

const WORK_PRIORITY = ["low", "medium", "high"] as const;
type WorkPriority = (typeof WORK_PRIORITY)[number];

export interface WorkSearch extends WorkspaceSearch {
  readonly q?: string;
  readonly status?: ReadonlyArray<WorkStatus>;
  readonly priority?: ReadonlyArray<WorkPriority>;
  readonly period?: "today" | "7d" | "30d" | "all";
}

export function validateWorkSearch(raw: Record<string, unknown>): WorkSearch {
  const base = WorkspaceSearchSchema.parse(raw);
  return {
    ...base,
    q: asString(raw.q),
    status: asEnumArray(raw.status, WORK_STATUS),
    priority: asEnumArray(raw.priority, WORK_PRIORITY),
    period: asPeriod(raw.period),
  };
}

// ---------------------------------------------------------------------------
// Sales / Marketing search schemas (Jun 2026 — CRM + campaigns verticals).
// Lean for now: cross-cutting params (proposal/focus/msg/sub) + free-text q.
// ---------------------------------------------------------------------------

export interface SalesSearch extends WorkspaceSearch {
  readonly q?: string;
}

export function validateSalesSearch(raw: Record<string, unknown>): SalesSearch {
  const base = WorkspaceSearchSchema.parse(raw);
  return { ...base, q: asString(raw.q) };
}

// ---------------------------------------------------------------------------
// Technical search schema.
// ---------------------------------------------------------------------------

const TECHNICAL_TYPES = ["drawing", "revision", "bom", "dxf"] as const;
type TechnicalType = (typeof TECHNICAL_TYPES)[number];

export interface TechnicalSearch extends WorkspaceSearch {
  readonly q?: string;
  readonly type?: ReadonlyArray<TechnicalType>;
  readonly material?: string;
  readonly period?: "today" | "7d" | "30d" | "all";
}

export function validateTechnicalSearch(raw: Record<string, unknown>): TechnicalSearch {
  const base = WorkspaceSearchSchema.parse(raw);
  return {
    ...base,
    q: asString(raw.q),
    type: asEnumArray(raw.type, TECHNICAL_TYPES),
    material: asString(raw.material),
    period: asPeriod(raw.period),
  };
}

// ---------------------------------------------------------------------------
// Internal — keep zod plugged so future fields can be ``z.object`` extended
// without an import. The helpers above hand-coerce since the URL surface
// is loose, but the underlying invariants stay close.
// ---------------------------------------------------------------------------

// Reference ``z`` so a stricter zod-only path stays available — a
// future PR will swap the hand-rolled coercers for ``z.object`` chains
// once every tab has shipped its endpoint contract.
export const _ZOD_TYPE_HOLDER = z;
